import React, { useState, useRef, useEffect } from 'react';
import '../styles/DocumentPicker.css';

function DocumentPicker({ isVisible, onDocumentSelect, onClose, isUploading: externalIsUploading }) {
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef(null);

  // Use external uploading state if provided, otherwise use internal state
  const isUploading = externalIsUploading !== undefined ? externalIsUploading : false;

  // Supported document types.
  const supportedTypes = {
    'application/pdf': 'PDF',
    'application/msword': 'DOC',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'DOCX',
    'application/vnd.ms-excel': 'XLS',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'XLSX',
    'application/vnd.ms-powerpoint': 'PPT',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'PPTX',
    'text/plain': 'TXT',
    'text/csv': 'CSV',
    'application/rtf': 'RTF',
    'application/zip': 'ZIP',
    'application/x-rar-compressed': 'RAR',
    'application/x-7z-compressed': '7Z'
  };

  const maxFileSize = 50 * 1024 * 1024; // 50MB limit for documents
  const maxFiles = 10; // Maximum number of files

  // Reset selected files when component becomes invisible
  useEffect(() => {
    if (!isVisible) {
      setSelectedFiles([]);
    }
  }, [isVisible]);

  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files);
    processFiles(files);
  };

  const processFiles = (files) => {
    const validFiles = [];
    const errors = [];

    files.forEach(file => {
      // Check file type
      if (!supportedTypes[file.type] && !isTextFile(file)) {
        errors.push(`${file.name}: Unsupported file type`);
        return;
      }

      // Check file size
      if (file.size > maxFileSize) {
        errors.push(`${file.name}: File too large (max 50MB)`);
        return;
      }

      // Check if we're not exceeding the maximum number of files
      if (selectedFiles.length + validFiles.length >= maxFiles) {
        errors.push(`Maximum ${maxFiles} files allowed`);
        return;
      }

      validFiles.push({
        file: file,
        id: Date.now() + Math.random(),
        name: file.name,
        size: file.size,
        type: supportedTypes[file.type] || getFileExtension(file.name).toUpperCase(),
        sizeFormatted: formatFileSize(file.size)
      });
    });

    if (errors.length > 0) {
      alert('Some files were not added:\n' + errors.join('\n'));
    }

    setSelectedFiles(prev => [...prev, ...validFiles]);
  };

  const isTextFile = (file) => {
    const textExtensions = ['.txt', '.csv', '.rtf', '.log', '.md'];
    return textExtensions.some(ext => file.name.toLowerCase().endsWith(ext));
  };

  const getFileExtension = (filename) => {
    return filename.slice((filename.lastIndexOf(".") - 1 >>> 0) + 2);
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const removeFile = (fileId) => {
    setSelectedFiles(prev => prev.filter(file => file.id !== fileId));
  };

  const handleSend = () => {
    if (selectedFiles.length > 0 && onDocumentSelect && !isUploading) {
      const files = selectedFiles.map(item => item.file);
      onDocumentSelect(files);
    }
  };

  const handleClose = () => {
    setSelectedFiles([]);
    onClose();
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (event) => {
    event.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setIsDragOver(false);
    const files = Array.from(event.dataTransfer.files);
    processFiles(files);
  };

  const openFileDialog = () => {
    fileInputRef.current?.click();
  };

  const getFileIcon = (type) => {
    switch (type) {
      case 'PDF':
        return (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" fill="#e74c3c"/>
            <polyline points="14,2 14,8 20,8" fill="#fff"/>
            <text x="12" y="16" textAnchor="middle" fill="white" fontSize="8" fontWeight="bold">PDF</text>
          </svg>
        );
      case 'DOC':
      case 'DOCX':
        return (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" fill="#2980b9"/>
            <polyline points="14,2 14,8 20,8" fill="#fff"/>
            <text x="12" y="16" textAnchor="middle" fill="white" fontSize="7" fontWeight="bold">DOC</text>
          </svg>
        );
      case 'XLS':
      case 'XLSX':
        return (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" fill="#27ae60"/>
            <polyline points="14,2 14,8 20,8" fill="#fff"/>
            <text x="12" y="16" textAnchor="middle" fill="white" fontSize="7" fontWeight="bold">XLS</text>
          </svg>
        );
      case 'PPT':
      case 'PPTX':
        return (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" fill="#e67e22"/>
            <polyline points="14,2 14,8 20,8" fill="#fff"/>
            <text x="12" y="16" textAnchor="middle" fill="white" fontSize="7" fontWeight="bold">PPT</text>
          </svg>
        );
      case 'ZIP':
      case 'RAR':
      case '7Z':
        return (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" fill="#9b59b6"/>
            <polyline points="14,2 14,8 20,8" fill="#fff"/>
            <text x="12" y="16" textAnchor="middle" fill="white" fontSize="7" fontWeight="bold">ZIP</text>
          </svg>
        );
      default:
        return (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" fill="#95a5a6"/>
            <polyline points="14,2 14,8 20,8" fill="#fff"/>
            <text x="12" y="16" textAnchor="middle" fill="white" fontSize="8" fontWeight="bold">FILE</text>
          </svg>
        );
    }
  };

  if (!isVisible) return null;

  return (
    <div className="document-picker-overlay">
      <div className="document-picker-container">
        {/* Header */}
        <div className="document-picker-header">
          <h3>Select Documents</h3>
          <button className="close-btn" onClick={handleClose}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18"/>
              <line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="document-picker-content">
          {/* Drop Zone */}
          <div 
            className={`drop-zone ${isDragOver ? 'drag-over' : ''}`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={openFileDialog}
          >
            <div className="drop-zone-content">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1">
                <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/>
                <polyline points="14,2 14,8 20,8"/>
                <line x1="16" y1="13" x2="8" y2="13"/>
                <line x1="16" y1="17" x2="8" y2="17"/>
                <line x1="10" y1="9" x2="8" y2="9"/>
              </svg>
              <p>Drop documents here or click to browse</p>
              <span className="file-types">
                Supported: PDF, DOC, DOCX, XLS, XLSX, PPT, PPTX, TXT, ZIP, etc.
              </span>
              <span className="file-limit">
                Max {maxFiles} files, 50MB each
              </span>
            </div>
          </div>

          {/* Selected Files */}
          {selectedFiles.length > 0 && (
            <div className="selected-files">
              <h4>Selected Files ({selectedFiles.length})</h4>
              <div className="file-list">
                {selectedFiles.map((fileItem) => (
                  <div key={fileItem.id} className="file-item">
                    <div className="file-icon">
                      {getFileIcon(fileItem.type)}
                    </div>
                    <div className="file-info">
                      <span className="file-name">{fileItem.name}</span>
                      <span className="file-details">
                        {fileItem.type} • {fileItem.sizeFormatted}
                      </span>
                    </div>
                    <button 
                      className="remove-file-btn"
                      onClick={() => removeFile(fileItem.id)}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <line x1="18" y1="6" x2="6" y2="18"/>
                        <line x1="6" y1="6" x2="18" y2="18"/>
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="document-picker-footer">
          <button 
            className="cancel-btn" 
            onClick={handleClose}
            disabled={isUploading}
          >
            Cancel
          </button>
          <button 
            className="send-btn" 
            onClick={handleSend}
            disabled={selectedFiles.length === 0 || isUploading}
          >
            {isUploading ? (
              <>
                <span className="upload-spinner"></span>
                Sending...
              </>
            ) : (
              `Send ${selectedFiles.length > 0 ? `(${selectedFiles.length})` : ''}`
            )}
          </button>
        </div>

        {/* Hidden File Input */}
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileSelect}
          multiple
          accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.rtf,.zip,.rar,.7z"
          style={{ display: 'none' }}
        />
      </div>
    </div>
  );
}

export default DocumentPicker;
